import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Mic,
  Shield,
  Clock,
  ChevronRight,
  Check,
  Star,
  Users,
  FileText,
  Target,
  Heart,
  Award,
  ArrowRight,
  Mail,
  Sparkles,
  Brain,
  Lock,
  Stethoscope,
  Activity,
  Smartphone,
  Zap,
  Send,
  TrendingUp,
  Layout,
  PenTool
} from 'lucide-react';
import Card from '../components/Card/Card';
import Button from '../components/buttons/Button';
import HeaderNoAuth from '../components/Header/HeaderNoAuth';
import './Landing.css';

const Landing = () => {
  const navigate = useNavigate();
  const { isSubscribed, profile } = useAuth();
  const isAuthenticated = isSubscribed || profile;
  const [email, setEmail] = useState('');
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const statsRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [stats, setStats] = useState({
    doctors: 0,
    reports: 0,
    accuracy: 0,
    timeSaved: 0
  });

  // ============================================
  // TÉMOIGNAGES - 6 avis utilisateurs
  // ============================================
  const testimonials = [
    {
      name: "Dr. M. Delafay",
      specialty: "Médecine générale",
      location: "Genève",
      rating: 5,
      text: "Après une semaine, Voxia formatait mes rapports exactement comme je les aime. Je dicte en vrac, tout est structuré automatiquement.",
      avatar: "MD",
      highlight: "Apprentissage rapide"
    },
    {
      name: "Dr. J. Laurent",
      specialty: "Cardiologie",
      location: "Lausanne",
      rating: 5,
      text: "J'ai importé mes templates de compte-rendu d'intervention. Maintenant je dicte, et le document sort avec mon en-tête, ma mise en page, prêt à signer. Un vrai gain de temps.",
      avatar: "JL",
      highlight: "Templates personnalisés"
    },
    {
      name: "Dr. I. Tornare",
      specialty: "Médecine d'urgence",
      location: "Fribourg",
      rating: 5,
      text: "Ce qui m'a convaincue : mes lettres aux gardent exactement le format que j'ai mis des années à perfectionner. Mon logo, mon en-tête, ma signature, tout est là.",
      avatar: "IT",
      highlight: "Identité préservée"
    },
    {
      name: "Dr. T. Keller",
      specialty: "Médecine générale",
      location: "Bâle",
      rating: 5,
      text: "La transcription multilingue est bluffante: 'Dyspnée d'effort NYHA III' transcrit correctement du suisse allemand aud français du premier coup.",
      avatar: "TK",
      highlight: "Vocabulaire médical"
    },
    {
      name: "Dr. E. Rossi",
      specialty: "Radiologie",
      location: "Lausanne",
      rating: 5,
      text: "Je dicte 40-50 rapports par jour. Voxia a compris ma structure en quelques heures. Maintenant, chaque rapport sort formaté comme je le veux. J'ai récupéré 2 heures par jour.",
      avatar: "ER",
      highlight: "2h gagnées/jour"
    },
    {
      name: "Dr. B. Weasley",
      specialty: "Cardiologie",
      location: "Fribourg",
      rating: 5,
      text: "J'utilise mon dictaphone Philips depuis 15 ans. Voxia l'intègre facilement et a appris mon style de dictée. Pas besoin de changer mes habitudes.",
      avatar: "BW",
      highlight: "Dictaphone Philips"
    }
  ];

  // ============================================
  // ÉTAPES DU PROCESSUS - Dictez, Envoyez, Améliorez
  // ============================================
  const processSteps = [
    {
      number: "1",
      title: "Dictez",
      description: "Parlez naturellement. Voxia transcrit et met en forme selon votre style.",
      icon: Mic,
      color: "#FF6B6B"
    },
    {
      number: "2",
      title: "Envoyez",
      description: "Votre rapport est prêt, formaté comme vous l'aimez. Relisez, signez, envoyez.",
      icon: Send,
      color: "#4ECDC4"
    },
    {
      number: "3",
      title: "Améliorez",
      description: "Voxia apprend de chaque rapport. Plus vous l'utilisez, plus elle vous ressemble.",
      icon: TrendingUp,
      color: "#45B7D1"
    }
  ];

  // ============================================
  // CE QUI CHANGE AVEC VOXIA
  // ============================================
  const benefits = [
    {
      icon: Mic,
      title: "Moins de frappe",
      description: "Dictez au lieu de taper, Voxia met en forme",
      color: "#FF6B6B",
      bgColor: "rgba(255, 107, 107, 0.12)"
    },
    {
      icon: Brain,
      title: "Vos habitudes respectées",
      description: "L'IA s'adapte à vous, pas l'inverse",
      color: "#9B59B6",
      bgColor: "rgba(155, 89, 182, 0.12)"
    },
    {
      icon: FileText,
      title: "Vos documents, votre style",
      description: "Templates Word avec votre identité visuelle",
      color: "#3498DB",
      bgColor: "rgba(52, 152, 219, 0.12)"
    },
    {
      icon: Target,
      title: "Précision médicale",
      description: "Vocabulaire spécialisé compris nativement",
      color: "#E74C3C",
      bgColor: "rgba(231, 76, 60, 0.12)"
    },
    {
      icon: Shield,
      title: "Données en Suisse",
      description: "Hébergement local, conformité LPD/RGPD",
      color: "#27AE60",
      bgColor: "rgba(39, 174, 96, 0.12)"
    },
    {
      icon: Zap,
      title: "Rapide à prendre en main",
      description: "Productif dès le premier jour",
      color: "#F39C12",
      bgColor: "rgba(243, 156, 18, 0.12)"
    }
  ];

  // ============================================
  // COMPARAISON VOXIA VS AMBIENT SCRIBE
  // ============================================
  const voxiaAdvantages = [
    {
      title: "Apprend votre style",
      description: "Voxia s'adapte à votre façon de rédiger automatiquement"
    },
    {
      title: "Vos templates Word",
      description: "Importez vos modèles avec logo et en-tête"
    },
    {
      title: "Transcription fidèle",
      description: "Ce que vous dictez = ce qui est écrit"
    },
    {
      title: "Données en Suisse",
      description: "Hébergement local, conformité LPD/RGPD"
    },
    {
      title: "Multiplateforme",
      description: "Web, desktop, mobile, dictaphones Philips/Olympus"
    }
  ];

  const ambientDisadvantages = [
    {
      title: "Configuration manuelle",
      description: "Paramétrage complexe et formats imposés"
    },
    {
      title: "Formats rigides",
      description: "Abandonnez vos templates pour leurs standards"
    },
    {
      title: "Risque d'hallucinations",
      description: "L'IA réécrit et peut inventer des informations"
    },
    {
      title: "Données à l'étranger",
      description: "Serveurs US, questions de conformité"
    },
    {
      title: "Un seul device",
      description: "Utilisable que sur ordinateur"
    }
  ];

  // Animation des statistiques au scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateStats();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [hasAnimated]);

  const animateStats = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setStats({
        doctors: Math.floor(120 * progress),
        reports: Math.floor(75000 * progress),
        accuracy: Math.floor(99.5 * progress * 10) / 10,
        timeSaved: Math.floor(90 * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setStats({
          doctors: 120,
          reports: 75000,
          accuracy: 99.5,
          timeSaved: 90
        });
      }
    }, interval);
  };

  // Rotation automatique des témoignages
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [testimonials.length]);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      sessionStorage.setItem('prefilledEmail', email);
      navigate('/register');
    }
  };

  return (
    <div className="landing-page no-padding">
      {/* Navigation */}
      {!isAuthenticated && <HeaderNoAuth />}

      {/* ============================================
          1. HERO SECTION
          ============================================ */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="floating-icons">
            <div className="floating-icon icon-1"><Activity size={40} /></div>
            <div className="floating-icon icon-2"><Heart size={35} /></div>
            <div className="floating-icon icon-3"><Brain size={45} /></div>
            <div className="floating-icon icon-4"><Shield size={38} /></div>
            <div className="floating-icon icon-5"><FileText size={42} /></div>
            <div className="floating-icon icon-6"><Stethoscope size={40} /></div>
            <div className="floating-icon icon-7"><Sparkles size={38} /></div>
            <div className="floating-icon icon-8"><Award size={36} /></div>
          </div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">
            Dictez, c'est transcrit.<br />
            <span className="hero-gradient">Exactement comme vous l'avez dit.</span>
          </h1>
          <p className="hero-subtitle">
            Voxia apprend votre style de rédaction : après quelques rapports, elle rédige et formate, exactement comme vous l'auriez fait vous-même.
          </p>
          <div className="hero-buttons">
            <Button
              variant="primary"
              type="default"
              size="large"
              icon={ArrowRight}
              onClick={() => navigate('/register')}
            >
              Essayer gratuitement
            </Button>
            <Button
              variant="secondary"
              type="default"
              size="large"
              onClick={() => document.getElementById('process').scrollIntoView({ behavior: 'smooth' })}
            >
              Comment ça marche
            </Button>
          </div>
          <div className="hero-trust">
            <div className="trust-item">
              <Shield size={20} />
              <span>Hébergé en Suisse</span>
            </div>
            <div className="trust-item">
              <Lock size={20} />
              <span>LPD / RGPD</span>
            </div>
            <div className="trust-item">
              <Award size={20} />
              <span>Créé par des médecins</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          2. PROCESSUS - Dictez, Envoyez, Améliorez
          ============================================ */}
      <section id="process" className="process-section">
        <div className="section-container-full">
          <div className="section-header-center">
            <h2>Dictez. Envoyez. Améliorez.</h2>
            <p>Un cycle vertueux qui s'améliore avec le temps</p>
          </div>
          <div className="process-steps">
            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="process-step">
                  <div className="step-card">
                    <div className="step-number" style={{ backgroundColor: step.color }}>
                      {step.number}
                    </div>
                    <div className="step-icon">
                      <IconComponent size={40} style={{ color: step.color }} />
                    </div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="step-arrow">
                      <ChevronRight size={30} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* ============================================
          4. TÉMOIGNAGES
          ============================================ */}
      <section className="testimonials-section">
        <div className="section-container-full">
          <div className="section-header-center">
            <h2>Ils utilisent Voxia au quotidien</h2>
            <p>Médecins en Suisse romande et alémanique</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className={`testimonial-card ${index === activeTestimonial ? 'active' : ''}`}
              >
                <div className="testimonial-highlight">
                  {testimonial.highlight}
                </div>
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="var(--color-warning)" stroke="var(--color-warning)" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{testimonial.avatar}</div>
                  <div className="author-info">
                    <h4>{testimonial.name}</h4>
                    <p>{testimonial.specialty}</p>
                    <p className="author-location">{testimonial.location}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          5. PLATEFORMES
          ============================================ */}
      <section className="platform-section">
        <div className="section-container-full">
          <div className="section-header-center">
            <h2>Là où vous travaillez</h2>
            <p>Une solution qui s'adapte à vos habitudes</p>
          </div>
          <div className="platform-grid">
            <Card className="platform-card">
              <div className="platform-icon">
                <FileText size={40} style={{ color: 'var(--color-primary)' }} />
              </div>
              <h3>Application Web</h3>
              <p>Accessible depuis n'importe quel navigateur. Rien à installer.</p>
            </Card>
            <Card className="platform-card">
              <div className="platform-icon">
                <Shield size={40} style={{ color: 'var(--color-success)' }} />
              </div>
              <h3>Application Bureau</h3>
              <p>Windows, Mac, Linux. Intégration directe avec votre workflow.</p>
            </Card>
            <Card className="platform-card">
              <div className="platform-icon">
                <Smartphone size={40} style={{ color: 'var(--color-info)' }} />
              </div>
              <h3>Mobile</h3>
              <p>iOS et Android. Dictez au chevet du patient.</p>
            </Card>
            <Card className="platform-card featured">
              <div className="platform-icon">
                <Mic size={40} style={{ color: 'var(--color-danger)' }} />
              </div>
              <h3>Dictaphones professionnels</h3>
              <p>Gardez votre équipement existant. Support natif Philips et Olympus.</p>
              <div className="compatible-devices">
                <span className="device-badge">Philips DPM</span>
                <span className="device-badge">SpeechMike</span>
                <span className="device-badge">Olympus</span>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* ============================================
          6. STATISTIQUES
          ============================================ */}
      <section ref={statsRef} className="stats-section">
        <div className="section-container-full">
          <div className="stats-grid">
            <div className="stat-item">
              <Users size={40} className="stat-icon" />
              <div className="stat-number">{stats.doctors}+</div>
              <div className="stat-label">Médecins en Suisse</div>
            </div>
            <div className="stat-item">
              <FileText size={40} className="stat-icon" />
              <div className="stat-number">{(stats.reports / 1000).toFixed(0)}k+</div>
              <div className="stat-label">Rapports générés</div>
            </div>
            <div className="stat-item">
              <Target size={40} className="stat-icon" />
              <div className="stat-number">{stats.accuracy}%</div>
              <div className="stat-label">Précision transcription</div>
            </div>
            <div className="stat-item">
              <Clock size={40} className="stat-icon" />
              <div className="stat-number">{stats.timeSaved} min</div>
              <div className="stat-label">Économisées par jour</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          7. COMPARAISON VOXIA VS AMBIENT SCRIBE
          ============================================ */}
      <section className="comparison-section">
        <div className="section-container-full">
          <div className="section-header-center">
            <h2>Voxia vs. solutions "Ambient Scribe"</h2>
            <p>Une approche différente de la dictée médicale</p>
          </div>
          <div className="comparison-grid">
            <Card className="comparison-card voxia-card">
              <div className="comparison-header">
                <Stethoscope size={32} style={{ color: 'var(--color-primary)' }} />
                <h3>Voxia</h3>
                <div className="badge-preferred">Notre approche</div>
              </div>
              <ul className="comparison-list">
                {voxiaAdvantages.map((item, index) => (
                  <li key={index}>
                    <Check size={20} style={{ color: 'var(--color-success)' }} />
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.description}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="comparison-card ambient-card">
              <div className="comparison-header">
                <Brain size={32} style={{ color: '#666' }} />
                <h3>Ambient Scribe</h3>
              </div>
              <ul className="comparison-list">
                {ambientDisadvantages.map((item, index) => (
                  <li key={index}>
                    <Target size={20} style={{ color: 'var(--color-danger)' }} />
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.description}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* ============================================
          8. DÉVELOPPÉ PAR DES MÉDECINS
          ============================================ */}
      <section className="rd-section">
        <div className="section-container-full">
          <Card className="rd-card">
            <div className="rd-content">
              <div className="rd-header">
                <Stethoscope size={48} style={{ color: 'var(--color-primary)' }} />
                <h2>Développée par des médecins, pour des médecins</h2>
              </div>
              <p className="rd-intro">
                Voxia est née de notre propre besoin. Nous sommes médecins, nous utilisons 
                Voxia chaque jour dans notre pratique, et nous l'améliorons en continu 
                selon notre expérience et vos retours.
              </p>
              <div className="rd-grid">
                <div className="rd-item">
                  <Activity size={32} style={{ color: 'var(--color-info)' }} />
                  <h3>Testée en conditions réelles</h3>
                  <p>Chaque fonctionnalité est validée dans notre pratique clinique quotidienne.</p>
                </div>
                <div className="rd-item">
                  <Brain size={32} style={{ color: 'var(--color-success)' }} />
                  <h3>Amélioration continue</h3>
                  <p>Vos retours alimentent directement nos priorités de développement.</p>
                </div>
                <div className="rd-item">
                  <Shield size={32} style={{ color: 'var(--color-primary)' }} />
                  <h3>Entreprise suisse pérenne</h3>
                  <p>Vision à long terme. Vos données et votre workflow sont sécurisés.</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

{/* ============================================
          9. CTA FINAL
          ============================================ */}
      <section className="cta-section">
        <div className="section-container-full">
          <Card className="cta-card">
            <div className="cta-content">
              <h2>Prêt à gagner du temps ?</h2>
              <p>Essayez Voxia avec vos propres documents. Voyez la différence.</p>
              <Button
                variant="primary"
                size="large"
                onClick={() => navigate('/register')}
              >
                Commencer gratuitement
              </Button>
              <p className="cta-disclaimer">
                30 jours gratuits • Annulation en 1 clic
              </p>
            </div>
          </Card>
        </div>
      </section>
      {/* ============================================
          FOOTER
          ============================================ */}
      <footer className="landing-footer">
        <div className="footer-container-full">
          <div className="footer-columns">
            <div className="footer-column">
              <div className="footer-logo">
                <Stethoscope size={32} />
                <span>voxia</span>
              </div>
              <p>Dictée médicale intelligente, développée par des médecins suisses.</p>
              <div className="footer-badges">
                <div className="badge-item">
                  <Shield size={20} />
                  <span>Swiss Made</span>
                </div>
                <div className="badge-item">
                  <Award size={20} />
                  <span>LPD/RGPD</span>
                </div>
              </div>
            </div>
            <div className="footer-column"></div>
            <div className="footer-column">
              <h4>Aide</h4>
              <ul>
                <li><a onClick={() => navigate('/faq')}>FAQ</a></li>
                <li><a onClick={() => navigate('/about')}>À propos</a></li>
                <li><a onClick={() => navigate('/contact')}>Contact</a></li>
              </ul>
            </div>
            <div className="footer-column">
              <h4>Légal</h4>
              <ul>
                <li><a onClick={() => navigate('/terms')}>CGU</a></li>
                <li><a onClick={() => navigate('/privacy')}>Confidentialité</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2025 GBM Medical SA. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;